"use client";

import React from "react";
import { ArrowRight, CircleQuestionMark as HelpCircle, ShieldCheck, Truck, Package } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Buy() {
  const shouldReduceMotion = useReducedMotion();
  const specifications = [{
    icon: Package,
    label: "PACKAGE INCLUSIONS",
    detail: "Check console, arena & blowers"
  }, {
    icon: ShieldCheck,
    label: "CONDITION RATING",
    detail: "Factory new or inspected used"
  }, {
    icon: Truck,
    label: "FREIGHT LOGISTICS",
    detail: "Destination & dock delivery specs"
  }];
  return <section id="howmuchtobuy" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border overflow-hidden">
      {/* Anchor compat for source instruction */}
      <span id="how-much-to-buy" className="sr-only" aria-hidden="true" />

      {/* Industrial datum grid lines & ambient aesthetic */}
      <div className="pointer-events-none absolute inset-0 select-none opacity-40">
        <div className="absolute inset-x-0 top-0 h-px bg-border" />
        <div className="absolute inset-y-0 left-6 sm:left-12 lg:left-24 w-px bg-border/60" />
        <div className="absolute inset-y-0 right-6 sm:right-12 lg:right-24 w-px bg-border/60" />
      </div>

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="space-y-8">
          {/* Machine Header & Datum Identifier */}
          <div className="border-l-2 border-primary pl-4 sm:pl-6">
            <div className="flex items-center gap-2 mb-2 text-xs font-mono tracking-widest text-muted-foreground uppercase">
              <HelpCircle className="size-3.5 text-primary" />
              <span>COMMERCIAL SPECIFICATION NOTE // PRICING DIRECTORY</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground">
              How Much Does a Mechanical Bull Cost to Buy?
            </h2>
          </div>

          {/* Metal Rating Plate Border Treatment Panel */}
          <div className="relative bg-card text-card-foreground border border-border shadow-xs">
            {/* Machined Corner Accents */}
            <div className="absolute -top-1 -left-1 size-2 border-t-2 border-l-2 border-primary" />
            <div className="absolute -top-1 -right-1 size-2 border-t-2 border-r-2 border-primary" />
            <div className="absolute -bottom-1 -left-1 size-2 border-b-2 border-l-2 border-primary" />
            <div className="absolute -bottom-1 -right-1 size-2 border-b-2 border-r-2 border-primary" />

            {/* Industrial Plate Header Bar */}
            <div className="flex items-center justify-between border-b border-border bg-muted/60 px-5 py-2.5 sm:px-8 text-xs font-mono text-muted-foreground tracking-wider">
              <span className="font-semibold text-foreground">RATING SPECIFICATION SHEET</span>
              <span className="hidden sm:inline">SERIAL REF: EQ-PRICE-GUIDE</span>
            </div>

            {/* Direct Answer Copy Body */}
            <div className="p-6 sm:p-8 lg:p-10 space-y-6 text-base sm:text-lg leading-relaxed text-foreground font-sans">
              <p className="font-medium">
                The total purchase cost depends on the exact mechanical bull, whether it is new or used, what equipment is included, its condition and the delivery arrangement. Instead of relying on one market-wide number, compare current machines and calculate the cost of the complete package you actually need.
              </p>

              <div className="h-px w-full bg-border" />

              <p className="text-muted-foreground">
                For example, the machine price may not tell you whether the safety arena, blower or accessories are included. Shipping can also vary by product and destination. As a result, two listings with similar headline prices may have very different total purchase costs.
              </p>

              {/* Specification Check Matrix */}
              <div className="pt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-border">
                {specifications.map((spec, i) => {
                const Icon = spec.icon;
                return <div key={spec.label} data-index={i} className="p-3 bg-muted/40 border border-border/80 flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Icon className="size-4 text-primary shrink-0" />
                        <span className="font-mono text-xs font-semibold tracking-wider text-foreground">
                          {spec.label}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground pl-6">
                        {spec.detail}
                      </span>
                    </div>;
              })}
              </div>
            </div>

            {/* Panel Footer & Primary Action */}
            <div className="border-t border-border bg-muted/30 px-6 py-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                COMMERCIAL INVENTORY // UPDATED UNIT PRICING
              </span>

              <Button asChild size="lg" className="w-full sm:w-auto font-serif font-bold uppercase tracking-wider text-base h-12 px-8 shadow-sm transition-transform active:scale-[0.99]">
                <Link to="#current-inventory-prices">
                  <span>VIEW CURRENT INVENTORY</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}